<template>
  <vx-card
    class="mt-base"
    title="Law Activity 1: What Type of Job in the Legal Sector Would Suit Me?"
  >
    <p class="mb-5">
      If you are unsure of what specific job in the legal field appeals to you, take the three short
      quizzes below to see which you are best suited to.
    </p>
    <vs-tabs alignment="fixed" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Would I be a good Lawyer?">
        <h4 class="my-5">Would I Make a Good Lawyer?</h4>

        <p class="mb-5">
          Law is one of the most sought-after careers. But are you suited to everything a career in
          the legal profession entails? Take our quick quiz to find out if you’re suited to the life
          of a lawyer!
        </p>
        <p class="mb-5">
          In this quiz, there are 10 statements for you to respond to. Your results will give you a
          pretty good perspective on whether this job would suit you.
        </p>
        <p class="mb-5">There are 3 possible responses to each question: Yes, No, and Neutral.</p>
        <p class="mb-5">
          Answer “Yes” if you agree with the statement enthusiastically.<br />
          Answer “No” if you disagree with the statement and the tasks mentioned are of no interest
          to you.<br />
          Answer “Neutral” if you have no strong feelings either way.
        </p>
        <p class="mb-5">Select your response by placing a ✔ in the appropriate column.</p>

        <table class="table default-table">
          <tr class="header">
            <th colspan="2" width="70%">Tasks</th>
            <th width="10%">Yes</th>
            <th width="10%">Neutral</th>
            <th width="10%">No</th>
          </tr>
          <tr class="body" v-for="(question, index) in questions[1]" :key="index">
            <td>{{ index }}</td>
            <td>{{ question.question }}</td>
            <td class="text-center" v-for="(answer, index2) in question.answers" :key="index2">
              <vs-radio
                :vs-name="'answer_1_' + index"
                v-model="data['question_1_' + index]"
                class="checkbox_answer multiline"
                :vs-value="answer"
                :disabled="!isEditable"
                :aria-label="question.question + ': ' + answer"
              ></vs-radio>
            </td>
          </tr>
        </table>

        <p class="mb-5">Now hit the Calculate Results button to calculate your score.</p>

        <p class="mb-5">Your score is:</p>
        <div class="overflow-hidden mb-5">
          <vs-input
            v-model="question_1_score"
            class="mr-5 float-left"
            :aria-label="`Part 1 score`"
            readonly
          /><vs-button color="success" type="border" @click="calculateScore(1)" v-if="isEditable"
            >Calculate Score</vs-button
          >
        </div>
        <p class="mb-5"><strong>Results:</strong></p>

        <p class="mb-5">0 to 9 points = Most likely, this isn’t the career for you.</p>
        <p class="mb-5">10 to 15 points = You are the right person for the job.</p>
        <p class="mb-5">16 to 20 points = This is the perfect career for you.</p>
      </vs-tab>
      <vs-tab label="Would I be a good Legal Secretary?">
        <h4 class="my-5">Would I Make a Good Legal Secretary?</h4>
        <p class="mb-5">
          Perhaps you are interested in working in the legal sector but not as a lawyer?
        </p>
        <p class="mb-5">
          A job as a Legal Secretary might be a good fit for you. Take the quiz below to get an idea
          of whether or not a role as a Legal Secretary would suit you.
        </p>
        <p class="mb-5">
          In this quiz, there are 10 statements for you to respond to. Your results will give you a
          pretty good perspective on whether this job would suit you.
        </p>
        <p class="mb-5">There are 3 possible responses to each question: Yes, No, and Neutral.</p>
        <p class="mb-5">
          Answer “Yes” if you agree with the statement enthusiastically.<br />
          Answer “No” if you disagree with the statement and the tasks mentioned are of no interest
          to you.<br />
          Answer “Neutral” if you have no strong feelings either way.
        </p>
        <p class="mb-5">Select your response by placing a ✔ in the appropriate column.</p>

        <table class="table default-table">
          <tr class="header">
            <th colspan="2" width="70%">Tasks</th>
            <th width="10%">Yes</th>
            <th width="10%">Neutral</th>
            <th width="10%">No</th>
          </tr>
          <tr class="body" v-for="(question, index) in questions[2]" :key="index">
            <td>{{ index }}</td>
            <td>{{ question.question }}</td>
            <td class="text-center" v-for="(answer, index2) in question.answers" :key="index2">
              <vs-radio
                :vs-name="'answer_2_' + index"
                v-model="data['question_2_' + index]"
                class="checkbox_answer multiline"
                :vs-value="answer"
                :disabled="!isEditable"
                :aria-label="question.question + ': ' + answer"
              ></vs-radio>
            </td>
          </tr>
        </table>

        <p class="mb-5">Now hit the Calculate Results button to calculate your score.</p>

        <p class="mb-5">Your score is:</p>
        <div class="overflow-hidden mb-5">
          <vs-input
            v-model="question_2_score"
            class="mr-5 float-left"
            :aria-label="`Part 2 score`"
            readonly
          /><vs-button color="success" type="border" @click="calculateScore(2)" v-if="isEditable"
            >Calculate Score</vs-button
          >
        </div>
        <p class="mb-5"><strong>Results:</strong></p>

        <p class="mb-5">0 to 9 points = Most likely, this isn’t the career for you.</p>
        <p class="mb-5">10 to 15 points = You are the right person for the job.</p>
        <p class="mb-5">16 to 20 points = This is the perfect career for you.</p>
      </vs-tab>
      <vs-tab label="Would I be a good Court/Legal Clerk?">
        <h4 class="my-5">Would I Make a Good Lawyer?</h4>

        <p class="mb-5">
          Perhaps you are still interested in working in the legal sector but not as a lawyer or a
          legal secretary?
        </p>
        <p class="mb-5">
          Then, a job as a Court or a Legal Clerk might be a good fit for you. Take the quiz below
          to get an idea of whether or not a role as a Court or a Legal Clerk would suit you.
        </p>
        <p class="mb-5">
          In this quiz, there are 10 statements for you to respond to. Your results will give you a
          pretty good perspective on whether this job would suit you.
        </p>
        <p class="mb-5">There are 3 possible responses to each question: Yes, No, and Neutral.</p>
        <p class="mb-5">
          Answer “Yes” if you agree with the statement enthusiastically.<br />
          Answer “No” if you disagree with the statement and the tasks mentioned are of no interest
          to you.<br />
          Answer “Neutral” if you have no strong feelings either way.
        </p>

        <p class="mb-5">Select your response by placing a ✔ in the appropriate column.</p>

        <table class="table default-table">
          <tr class="header">
            <th colspan="2" width="70%">Tasks</th>
            <th width="10%">Yes</th>
            <th width="10%">Neutral</th>
            <th width="10%">No</th>
          </tr>
          <tr class="body" v-for="(question, index) in questions[3]" :key="index">
            <td>{{ index }}</td>
            <td>{{ question.question }}</td>
            <td class="text-center" v-for="(answer, index2) in question.answers" :key="index2">
              <vs-radio
                :vs-name="'answer_3_' + index"
                v-model="data['question_3_' + index]"
                class="checkbox_answer multiline"
                :vs-value="answer"
                :disabled="!isEditable"
                :aria-label="question.question + ': ' + answer"
              ></vs-radio>
            </td>
          </tr>
        </table>

        <p class="mb-5">Now hit the Calculate Results button to calculate your score.</p>

        <p class="mb-5">Your score is:</p>
        <div class="overflow-hidden mb-5">
          <vs-input
            v-model="question_3_score"
            class="mr-5 float-left"
            :aria-label="`Part 3 score`"
            readonly
          /><vs-button color="success" type="border" @click="calculateScore(3)" v-if="isEditable"
            >Calculate Score</vs-button
          >
        </div>
        <p class="mb-5"><strong>Results:</strong></p>

        <p class="mb-5">0 to 9 points = Most likely, this isn’t the career for you.</p>
        <p class="mb-5">10 to 15 points = You are the right person for the job.</p>
        <p class="mb-5">16 to 20 points = This is the perfect career for you.</p>
      </vs-tab>
    </vs-tabs>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      questions: {
        1: {
          1: {
            question: 'I would like to prepare and critically review contracts between parties.',
            answers: [2, 1, 0],
          },
          2: {
            question:
              'I would like to Interview new clients and meet with existing clients to provide legal advice.',
            answers: [2, 1, 0],
          },
          3: {
            question:
              'I would like to provide advice on family law, company law, partnerships, commercial law and trusts.',
            answers: [2, 1, 0],
          },
          4: {
            question:
              'I would like to draft legal documents, including motions, briefs, contracts of sale, mortgage documents, lease documents and wills.',
            answers: [2, 1, 0],
          },
          5: {
            question:
              'I would like to perform legal research to determine how the facts of a case interact with current law.',
            answers: [2, 1, 0],
          },
          6: {
            question: 'I would like to provide written advice and opinions on points of law.',
            answers: [2, 1, 0],
          },
          7: {
            question: 'I would like to negotiate on behalf of clients.',
            answers: [2, 1, 0],
          },
          8: {
            question: 'I would like to keep up to date with new developments in legislation.',
            answers: [2, 1, 0],
          },
          9: {
            question: 'I would like to represent clients in court or tribunal hearings.',
            answers: [2, 1, 0],
          },
          10: {
            question:
              "I would like to follow up on court decisions and settlements to ensure that all parties do what they've committed to or been ordered to do.",
            answers: [2, 1, 0],
          },
        },
        2: {
          1: {
            question:
              'I would like to liaise with other staff to arrange meetings and to gain and provide information.',
            answers: [2, 1, 0],
          },
          2: {
            question:
              'I would like to prepare reports, briefing notes, and correspondence and proofread work for typographical and grammatical errors.',
            answers: [2, 1, 0],
          },
          3: {
            question: 'I would like to maintain diaries and make travel arrangements.',
            answers: [2, 1, 0],
          },
          4: {
            question:
              'I would like to process incoming and outgoing mail, file correspondence, and maintain records.',
            answers: [2, 1, 0],
          },
          5: {
            question:
              'I would like to answer telephone calls, respond to inquiries, and redirect callers.',
            answers: [2, 1, 0],
          },
          6: {
            question:
              'I would like to take and transcribe the dictation of letters and other documents.',
            answers: [2, 1, 0],
          },
          7: {
            question:
              'I would like to greet visitors, find out the nature of their business, and direct them to appropriate personnel.',
            answers: [2, 1, 0],
          },
          8: {
            question: 'I would like to implement management decisions.',
            answers: [2, 1, 0],
          },
          9: {
            question: 'I would like to maintain records of meetings.',
            answers: [2, 1, 0],
          },
          10: {
            question:
              'I would like to mail, fax, or arrange for the delivery of legal correspondence to clients, witnesses, and court officials.',
            answers: [2, 1, 0],
          },
        },
        3: {
          1: {
            question:
              'I would like to list actions for hearing and processing documentation for court actions.',
            answers: [2, 1, 0],
          },
          2: {
            question:
              'I would like to document details of court proceedings, actions, and decisions.',
            answers: [2, 1, 0],
          },
          3: {
            question:
              'I would like to enforce the law as an officer of the court by executing court orders such as eviction notices.',
            answers: [2, 1, 0],
          },
          4: {
            question:
              'I would like to serve legal orders and documents such as summonses and subpoenas.',
            answers: [2, 1, 0],
          },
          5: {
            question:
              'I would like to organise jury and witness lists and to summons and swear-in juries and witnesses.',
            answers: [2, 1, 0],
          },
          6: {
            question:
              'I would like to maintain order in court and hearing rooms, and adjacent areas.',
            answers: [2, 1, 0],
          },
          7: {
            question:
              'I would like to assist Solicitors in areas of conveyancing, contracts, common law, probate, and other legal practice matters.',
            answers: [2, 1, 0],
          },
          8: {
            question:
              'I would like satisfying statutory requirements, establishing beneficial entitlements, and distributing assets.',
            answers: [2, 1, 0],
          },
          9: {
            question:
              'I would like to maintain probate and trust files, investing trust funds, and administering accounts.',
            answers: [2, 1, 0],
          },
          10: {
            question:
              'I would like to assist legal representatives with contracts, common law matters, certifications, and other legal practice concerns.',
            answers: [2, 1, 0],
          },
        },
      },
      question_1_score: 0,
      question_2_score: 0,
      question_3_score: 0,
    };
  },
  methods: {
    save() {
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
    calculateScore(index) {
      let score = 0;

      for (let i = 1; i <= 10; i++) {
        if (typeof this.data['question_' + index + '_' + i] !== 'undefined') {
          score += this.data['question_' + index + '_' + i];
        }
      }
      this['question_' + index + '_score'] = score;
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    this.calculateScore(1);
    this.calculateScore(2);
    this.calculateScore(3);
  },
};
</script>
